import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { TimesheetService } from './timesheet.service';
import {
    CreateTimesheetSetupInput,
    EnterUserHours,
    GetSingleTimesheetRequest,
    GetTimesheetByReviewer,
    GetTimesheetProgram,
    GetTimesheetProgramId,
    GetTimesheetsByApprovers,
    GetTimesheetsByUser,
    GetTimesheetWWU,
    SubmitTimesheet,
    UpdateUserHours
} from './timesheet.actions';
import { catchError, tap } from 'rxjs';
import {
    IApproverResponse,
    IPrograms,
    IReviewer,
    ISingleTimesheet,
    ITimeSheet,
    ITimesheetUsers
} from './timesheet.interface';

export interface IProps {
    timesheets: ITimeSheet[];
    programs: IPrograms[];
    users: ITimesheetUsers[];
    timesheet: ISingleTimesheet | null;
    approverRes: IApproverResponse[];
    reviewer: IReviewer[];
    loading: boolean;
    loadingSingle: boolean;
    error: string | null;
}

const defaults: IProps = {
    timesheets: [],
    programs: [],
    users: [],
    approverRes: [],
    reviewer: [],
    timesheet: null,
    loading: false,
    loadingSingle: false,
    error: null
};

@State<IProps>({
    name: 'timesheet',
    defaults
})
@Injectable()
export class TimeSheetState {
    timesheetService: TimesheetService = inject(TimesheetService);
    private store: Store = inject(Store);

    @Selector()
    static getTimesheets(state: IProps) {
        return state.timesheets;
    }
    @Selector()
    static getTimesheetsApprovers(state: IProps) {
        return state.approverRes;
    }
    @Selector()
    static getTimesheetsReviewer(state: IProps) {
        return state.reviewer;
    }
    @Selector()
    static getTimesheet(state: IProps) {
        return state.timesheet;
    }
    @Selector()
    static getPrograms(state: IProps) {
        return state.programs;
    }
    @Selector()
    static getProgramUsers(state: IProps) {
        return state.users;
    }
    @Selector()
    static loading(state: IProps) {
        return state.loading;
    }

    @Action(GetTimesheetProgram)
    getProgramIds(ctx: StateContext<IProps>, action: GetTimesheetProgram) {
        return this.timesheetService.getProgramId(action.program).pipe(
            tap((res) => {
                ctx.patchState({
                    programs: res,
                    error: null
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(GetTimesheetProgramId)
    getUserByProgramId(ctx: StateContext<IProps>, action: GetTimesheetProgramId) {
        ctx.patchState({ loading: true });

        return this.timesheetService.getUserByProgramId(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    users: res.data,
                    error: null,
                    loading: false
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message
                });
                throw error;
            })
        );
    }
    @Action(GetTimesheetWWU)
    getWwuUsersByProgramId(ctx: StateContext<IProps>, action: GetTimesheetWWU) {
        return this.timesheetService.getWwuUsersByProgramId(action.programId).pipe(
            tap((res) => {
                ctx.patchState({
                    error: null
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(CreateTimesheetSetupInput)
    createTimesheet(ctx: StateContext<IProps>, action: CreateTimesheetSetupInput) {
        return this.timesheetService.createTimesheetSetup(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    error: null
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }
    @Action(EnterUserHours)
    enterUserHours(ctx: StateContext<IProps>, action: EnterUserHours) {
        const state = ctx.getState();
        return this.timesheetService.enterUsersHours(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    users: state.users?.map((user) =>
                        user.email === action.payload.email ? { ...user, ...res } : user
                    ),
                    error: null
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }
    @Action(UpdateUserHours)
    updateUserHours(ctx: StateContext<IProps>, action: UpdateUserHours) {
        const state = ctx.getState();
        return this.timesheetService.updateUsersHours(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    users: state.users?.map((user) =>
                        user.id === action.payload.userId ? { ...user, ...action.payload } : user
                    ),
                    error: null
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }
    @Action(GetSingleTimesheetRequest)
    getOneRequest(ctx: StateContext<IProps>, action: GetSingleTimesheetRequest) {
        const state = ctx.getState();
        ctx.patchState({ loadingSingle: true });

        return this.timesheetService.getSingleTimesheet(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    timesheet: res,
                    loadingSingle: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loadingSingle: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
    @Action(GetTimesheetByReviewer)
    getByReviewer(ctx: StateContext<IProps>, action: GetTimesheetByReviewer) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.timesheetService.getTimesheetByReviewer(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    reviewer: res,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(SubmitTimesheet)
    submitTimesheet(ctx: StateContext<IProps>, action: SubmitTimesheet) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.timesheetService.submitTimesheet(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetTimesheetsByApprovers)
    getByApprovers(ctx: StateContext<IProps>, action: GetTimesheetsByApprovers) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.timesheetService.getTimesheetsByApprovers(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    approverRes: res,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetTimesheetsByUser)
    getByUser(ctx: StateContext<IProps>, action: GetTimesheetsByUser) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.timesheetService.getTimesheetBysUser(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    timesheets: res,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
}
